import React, {
  useState,
  useEffect,
  useCallback,
}                            from 'react';
import { useHistory }        from 'react-router-dom';
import { FontAwesomeIcon }   from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import ReCAPTCHA             from "react-google-recaptcha";
import { useUserAgent }      from 'hooks/useUserAgent';
import {
  FormattedMessage,
  useIntl,
}                           from 'react-intl';

const MAPPEX_URL = process.env.MAPP_EXTERNALS_BASE_URL;
//const BASE_API_URL = process.env.MAPP_API_BASE_URL;

const IEError = () => (
  <h2
    style=
    "color:var(--accent-6)"
  >
    <FormattedMessage defaultMessage="Hemos detectado que estás usando Internet Explorer" />
    <FormattedMessage
      defaultMessage="Por favor, usa {link1} o {link2}"
      values={{
        link1: <a href="https://www.mozilla.org/es-ES/firefox/"> Firefox </a>,
        link2: <a href="https://www.google.com/intl/es_ALL/chrome/"> Google Chrome </a>,
      }}
    />
  </h2>
);

const Home = () => {

  const { is_mobile } = useUserAgent();
  const { formatMessage } = useIntl();

  const FIELDS = [{
    formatted: formatMessage({ defaultMessage: 'Email' }),
    name: 'email',
  }, {
    formatted: formatMessage({ defaultMessage: 'Contraseña' }),
    name: 'password',
  }];

  const [form, setForm] = useState(FIELDS.reduce((acc, { name }) => ({ ...acc, [name]: '' }), {}));
  const [loading, setLoading] = useState(false);
  const [show_password, setShowPassword] = useState(false);
  const [captcha_token, setCaptchaToken] = useState(null);
  const [captcha_key, setCaptchaKey] = useState(null);
  const [error, setError] = useState(null);
  const recaptchaRef = React.createRef();
  //const token = localStorage.getItem('decoded_token');

  /* useEffect(() => {
*   if (token) {
*     const { dealer_id } = JSON.parse(token);
*     mfetch(`${BASE_API_URL}dealers/${dealer_id}/users?limit=1`)
*       .then(() => window.location = '/crm')
*       .catch(() => localStorage.removeItem('decoded_token'));
*   }
* }, [token]); */

  const displayUnblockCaptcha = async () => {
    await fetch(`${MAPPEX_URL}services/google/captcha/sitekey/unblock_login`)
      .then(res => res.ok && res.text() || Promise.reject(`Unexpected status ${res.status} at ${res.url}`))
      .then(setCaptchaKey);
  };

  const history = useHistory();

  const onLogin = useCallback(() => {
    setLoading(true);
    fetch('/auth/basic_cookie', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        ...form,
        captcha_response: captcha_token,
      }),
    })
      .then(res => res.ok && res.json() || res.text().then(t =>
        Promise.reject({
          message: t,
          login_token: res.headers.get('x-login-token'),
        })
      ))
      .then((decoded_token) => {
        if (decoded_token) {
          localStorage.setItem('decoded_token', JSON.stringify(decoded_token));
          if (localStorage.getItem('last_path')) {
            const last_path = localStorage.getItem('last_path');
            localStorage.removeItem('last_path');
            window.location.replace(last_path);
          } else {
            window.location.replace(is_mobile ? '/mobile' : '/crm');
          }
        }
      })
      .catch(err => {
        if (captcha_key) {
          recaptchaRef?.current?.reset();
          setCaptchaToken(null);
        }
        if (err.message === 'MFA_NEEDED') {
          return history.replace(`/unauthenticated/mfa?${new URLSearchParams({ token: err.login_token })}`);
        }
        if (err.message === 'CAPTCHA_NEEDED') return displayUnblockCaptcha();
        setError(err.message);
      })
      .then(() => setLoading(false));
  }, [form, captcha_token, captcha_key, history, recaptchaRef, is_mobile]);

  useEffect(() => {
    const onEnter = e => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        onLogin();
      }
    };

    window.addEventListener('keydown', onEnter);
    return () => window.removeEventListener('keydown', onEnter);
  }, [onLogin]);

  if (/Trident/.test(window.navigator.userAgent))
    return <IEError/>;

  return (
    <>
      <div
        style={{
          backgroundColor: Colors.background_light_1,
          borderRadius: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          height: '8.6em',
          width: '8.6em',
          boxShadow: `0px 0px 6px 1px ${Colors.background_light_1}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          src={'/static/1024.png'}
          alt="Walcu"
          style={{
            width: '8em',
            height: '8em',
            display: 'block',
          }}
        />
        <div style={{
          opacity: 0,
          top: 0,
          transform: 'translate(8em, -3em), rotate(45deg)',
          width: '0',
          height: '220px',
          position: 'absolute',
          zIndex: 1,
          animation: 'slide 9s linear 1s infinite normal',
          boxShadow: `0px 0px 25px 10px ${Colors.background_light_1}`,
        }}/>
      </div>
      <h2>
        <i>Go get 'em</i>
      </h2>
      {FIELDS.map(field => (
        <div
          key={field.name}
          style={{
            width: '15%',
            minWidth: '5em',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5em',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <label style={{ fontWeight: Fonts.weight.bold }}>
            {field.formatted}
          </label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              minWidth: '12em',
              gap: '0.25em',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <div style={{ flex: 1 }} />
            <input
              type={show_password ? 'text' : field.name}
              value={form[field.name]}
              onChange={e => setForm({ ...form, [field.name]: e.target.value })}
              style={{
                padding: '1.5em',
                flex: 10,
              }}
              className="input"
            />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {field.name === 'password' && (
                <FontAwesomeIcon
                  icon={show_password ? faEyeSlash : faEye}
                  style={{
                    cursor: 'pointer',
                    color: Colors.content_4,
                    justifySelf: 'flex-end',
                  }}
                  onClick={() => setShowPassword(!show_password)}
                />
              )}
            </div>
          </div>
        </div>
      ))}
      <button
        className='button-medium button-primary'
        style={{padding: '1em 2em'}}
        onClick={onLogin}
        disabled={loading}
      >
        <FormattedMessage defaultMessage='Entrar'/>
      </button>
      {captcha_key && (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={captcha_key}
          onChange={setCaptchaToken}
        />
      )}
      {error && (
        <div style={{ fontWeight: Fonts.weight.bold, color: Colors.accent_6 }}>
          {error}
        </div>
      )}
      <a
        onClick={() => history.push(`/unauthenticated/reset-password-request?${new URLSearchParams({ email: form.email })}`)}
      >
        <FormattedMessage defaultMessage="¿Has olvidado tu contraseña?" />
      </a>
    </>
  );
};

export default Home;
